/* eslint-disable curly */
/* eslint-disable prettier/prettier */
import { provideApolloClient } from "@vue/apollo-composable";
import { getPage, getPages, getSlugInfo , apolloClient } from "@/core/api/graphql";
import { globals } from "@/core/globals";
import { Logger } from "@/core/utilities";
import { JobSource     } from "../types";
import type { IPage } from "../../shared";
import type {ICareersJobPostingPage, IJobLocationModel, JobLocationItemType, JobOpportunityType, WorkableResponseModelType} from "../types";
import type { PageType } from "@/core/api/graphql/types";


//Special keys that for location dropdown to show either all location all all US state locations
export const locationKeyAllLocations = "AllLocations";
export const locationKeyAllUS = "AllUS";
export const urlSegmentAllUS = "united-states";

export function generateJobLocationsDropdownItems(
  locations: IJobLocationModel[],
  allLocationsLabel: string,
): JobLocationItemType[]
{
  const jobLocationDropdownItems: JobLocationItemType[] = locations
  .filter((jobLocation) => !(jobLocation.country === "United States" && jobLocation.state === locationKeyAllUS))
  .map((jobLocation) =>
  {
    return {
      label: jobLocation.label ?? (jobLocation.country === "United States" ? `(US) ${jobLocation.state}` : jobLocation.country),
      value: jobLocation.country === "United States" ? jobLocation.state : jobLocation.country,
      state: jobLocation.state,
      country: jobLocation.country,
      urlSegment: jobLocation.urlSegment,
      permalink: jobLocation.permalink,
      contentLinkId: jobLocation.contentLinkId,
    } as JobLocationItemType;
  });
  //Add all US locations option to the dropdown
  const unitedStatesParent = locations.find(
    (location) => location.country === "United States" && location.state === locationKeyAllUS,
  );

  jobLocationDropdownItems.unshift({
    label: "United States",
    value: locationKeyAllUS,
    state: undefined,
    country: "United States",
    urlSegment: unitedStatesParent?.urlSegment ?? urlSegmentAllUS,
    permalink: unitedStatesParent?.permalink,
    contentLinkId: unitedStatesParent?.contentLinkId,
  });

  //Add "All Locations" option to the dropdown
  jobLocationDropdownItems.unshift({
    label: allLocationsLabel,
    value: locationKeyAllLocations,
    state: "",
    country: "",
  });

  return jobLocationDropdownItems;
}

export function convertJobStateAndCountryToDropdownKey(state: string | undefined, country: string | undefined): string
{
  //If everything is empty, return all locations key
  if (!state && !country) {
    return locationKeyAllLocations;
  }

  //If country is US, but no state, return key for all US locations
  if (country === "United States" && !state) {
    return locationKeyAllUS;
  }

  //If we have US and a state, return key for that state
  if (country === "United States" && state) {
    return state;
  }

  //Case for non US locations, return the country name (e.g. Canada, China, etc.)
  if (country) {
    return country;
  }

  //This should never happen, everything should be handled above
  return locationKeyAllLocations;
}

/**
 * Gets the page by first getting url info with getSlugInfo and then
 * retrieving actual page by using retrieved ID from slugInfo;
 */
export async function getPageByUrlAsync<TPage extends IPage>(url: string) : Promise<TPage | undefined>
{
  if (!url)
    return undefined;

  //Get slug info using URL to get ID of the page
  const { storeId, cultureName } = globals;
  const slugInfoResult = await getSlugInfo({
    storeId,
    cultureName,
    slug: url,
  });

  if (!slugInfoResult?.slugInfo?.entityInfo?.objectId)
    return undefined;

  //Get the actual page using retrieved ID
  const pageResult = await provideApolloClient(apolloClient)(() => getPage({
    storeId,
    cultureName,
    id: slugInfoResult?.slugInfo?.entityInfo?.objectId ?? "",
  }));

  if (!pageResult?.page?.content)
    return undefined;

  return JSON.parse(pageResult.page.content) as TPage;
}

export async function fetchWorkableJobsAsJobOpportunitiesAsync(
  activeLocations: IJobLocationModel[] = [],
): Promise<JobOpportunityType[]>
{
  try {
    const workableURL = globals.workableJobsApiURL;

    if (!workableURL)
      throw "Workable URL is not set in the global variables. Check appsettings.json in Virto platform that it contains Workable:JobsApiURL setting.";

    const workableResponse = (await fetch(workableURL)) as Response;

    if (!workableResponse?.ok)
      throw `Failed to fetch data from Workable API: ${workableResponse.status} - ${workableResponse.statusText} - ${workableResponse.url}`;

    const workableResponseData = (await workableResponse.json()) as WorkableResponseModelType;

    if (!workableResponseData?.jobs)
      return [];

    //Convert response job models so that for each separate location we have a separate job opportunity object
    return filterWorkableJobsByLocations(workableResponseData, activeLocations);

  }
  catch (error)
  {
    Logger.error("Error while fetching from :", error);
  }

  return [];
}

export function filterWorkableJobsByLocations(
  workableResponseData: WorkableResponseModelType,
  activeLocations: IJobLocationModel[],
): JobOpportunityType[]
{
  return workableResponseData.jobs.flatMap((job) =>
    {
      return job.locations.flatMap((jobLocation) =>
      {
        const activeLocation = findJobLocationByCountryAndState(jobLocation.country, jobLocation.region, activeLocations);

        if (!activeLocation) {
          return [];
        }

        return [{
          code: job.code,
          shortcode: job.shortcode,
          title: job.title,
          department: job.department,
          employmentType: job.employment_type,
          locationString: undefined,
          country: jobLocation.country,
          state: jobLocation.region,
          city: jobLocation.city,
          url: job.url,
          jobSource: JobSource.Workable
        } as JobOpportunityType];
      });
    });
}

export function mapJobPostingPageToJobOpportunity(jobPostingPage : ICareersJobPostingPage)
{
  return {
    code: `${jobPostingPage.jobID}`,
    shortcode: `${jobPostingPage.jobID}`,
    title: jobPostingPage.title,
    department: undefined,
    employmentType: undefined,
    locationString: jobPostingPage.location,
    country: undefined, //Job posting pages do not have location details, only location string where everything is together
    state: undefined,
    city: undefined,
    url: jobPostingPage.permalink,
    jobSource: JobSource.Database
  } as JobOpportunityType;
}

export async function fetchPageByOptiIDAsync<TPage extends IPage>(contentTypes: string[], optiLinkID: string) : Promise<TPage | undefined>
{
  let keywordQuery = "";

  if (contentTypes && contentTypes.length > 0)
    keywordQuery = keywordQuery + ` contenttypes:${contentTypes.join(",")}`;

  if (contentTypes && contentTypes.length > 0)
    keywordQuery = keywordQuery + ` optiLinkID:${optiLinkID}`;

  const pageResults: PageType[] = await provideApolloClient(apolloClient)(() =>
    getPages({
      storeId: globals.storeId,
      cultureName: globals.cultureName,
      first: 1,
      keyword: keywordQuery,
    })
  );

  if (!pageResults || pageResults.length === 0)
  {
    return undefined;
  }

  return pageResults.map((result) => JSON.parse(result.content) as TPage).at(0);
}

export async function fetchCareerJobPostingPageByJobIDAsync(jobID: string) : Promise<ICareersJobPostingPage | undefined>
{

  const dbJobResults: PageType[] = await provideApolloClient(apolloClient)(() =>
    getPages({
      storeId: globals.storeId,
      cultureName: globals.cultureName,
      first: 1,
      keyword: `contenttypes:CareersJobPostingPage jobID:${jobID}`,
    })
  );

  if (!dbJobResults || dbJobResults.length === 0)
  {
    Logger.warn(`No database career job posting pages retrieved for ID: ${jobID}`);
    return;
  }

  return dbJobResults.map((result) => JSON.parse(result.content) as ICareersJobPostingPage).at(0);
}

export function isStrEqCI(str1: string | undefined, str2: string | undefined): boolean
{
  if (!str1 || !str2)
    return false;

  return str1.toLowerCase() === str2.toLowerCase();
}

export function findJobLocationByCountryAndState(
  country: string | undefined,
  state: string | undefined,
  locations: IJobLocationModel[],
) : IJobLocationModel | undefined
{
  if (!country && !state)
    return undefined;

  if (isStrEqCI(country, "United States"))
  {
    const normalizedState = state || locationKeyAllUS;
    const unitedStatesParent = locations.find(
      (location) => isStrEqCI(location.country, "United States") && isStrEqCI(location.state, locationKeyAllUS),
    );
    const stateLocation = locations.find(
      (location) => isStrEqCI(location.country, "United States") && isStrEqCI(location.state, normalizedState),
    );

    return unitedStatesParent && stateLocation ? stateLocation : undefined;
  }

  //Case for non US locations, just return the country
  return locations.find((location) => isStrEqCI(location.country, country));
}

